import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Fade,
  Dialog,
  DialogContent,
  Stack,
} from "@mui/material";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import api from "../axiosInstance";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ReplayIcon from "@mui/icons-material/Replay";

const formatDateParts = (dateInput) => {
  if (!dateInput) return { date: "-", time: "" };
  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) return { date: "-", time: "" };

  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return { date, time: `${hours}:${minutes} ${ampm}` };
};

const formatMs = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
};

const formatDuration = (startedAt, finishedAt) => {
  if (!startedAt || !finishedAt) return "—";
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  return formatMs(ms);
};

// Backend reprocess stage statuses: QUEUED, PROCESSING, PROCESSED, FAILED, PENDING
const STATUS_META = {
  QUEUED: { bg: "#eef2f6", color: "#546e7a", icon: ScheduleIcon },
  PROCESSING: { bg: "#fff3e0", color: "#ef6c00", icon: AutorenewIcon },
  PROCESSED: { bg: "#e8f5e9", color: "#2e7d32", icon: CheckCircleIcon },
  FAILED: { bg: "#fdecea", color: "#c62828", icon: ErrorIcon },
  PENDING: { bg: "#eef2f6", color: "#546e7a", icon: ScheduleIcon },
};
const DEFAULT_STATUS_META = { bg: "#f0f0f0", color: "#757575", icon: null };

const getStatusMeta = (status) => STATUS_META[status] || DEFAULT_STATUS_META;

const StatusChip = ({ status }) => {
  const meta = getStatusMeta(status);
  const Icon = meta.icon;
  return (
    <Chip
      label={status || "UNKNOWN"}
      size="small"
      icon={Icon ? <Icon sx={{ fontSize: 14 }} /> : undefined}
      sx={{
        bgcolor: meta.bg,
        color: meta.color,
        fontWeight: 600,
        fontSize: "0.7rem",
        borderRadius: 5,
        "& .MuiChip-icon": { color: meta.color },
      }}
    />
  );
};

const ReportSection = ({ label, data }) => {
  if (!data) return null;
  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {label}
        </Typography>
        {data.outcome && <StatusChip status={data.outcome} />}
      </Box>
      <Typography variant="body2" color="text.secondary">
        {data.summary || "No summary available."}
      </Typography>
      {Number.isFinite(data.durationMs) && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5 }}
        >
          Duration: {formatMs(data.durationMs)}
        </Typography>
      )}
    </Box>
  );
};

const ROW_ICON_COLORS = ["#7c3aed", "#16a34a", "#f59e0b", "#2563eb"];

const BookReprocessStatus = () => {
  const [rows, setRows] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailsRow, setDetailsRow] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  const fetchStatus = () => {
    setIsLoading(true);
    setError(null);
    api
      .get("/parse/reprocess", { params: { page, size: rowsPerPage } })
      .then((response) => {
        const data = response.data?.data;
        setRows(data?.rows || []);
        setTotalElements(data?.total || 0);
      })
      .catch((err) => {
        console.error("Error fetching reprocess status:", err);
        setError(
          err.response?.data?.errorMessage ||
            "Failed to load reprocess status. Please try again.",
        );
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const hasReportDetails = (row) =>
    Boolean(row.report || row.epubReport || row.indexReport);

  const handleRetry = async (row) => {
    setRetryingId(row.id);
    try {
      await api.post(`/parse/reprocess/rows/${row.id}/retry`);
      toast.success(`Retry triggered for "${row.bookName}"`, {
        autoClose: 2000,
      });
      fetchStatus();
    } catch (err) {
      console.error("Error retrying reprocess row:", err);
      toast.error(
        err.response?.data?.errorMessage ||
          "Failed to retry. Please try again.",
      );
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, textAlign: "left" }}>
      <ToastContainer />
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          to="/booklist"
          style={{ textDecoration: "none", color: "#17479e", fontWeight: 700 }}
        >
          Books List
        </Link>
        <Typography color="text.primary">Reprocess Status</Typography>
      </Breadcrumbs>

      {/* Header */}
      {/* <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, lineHeight: 1.2, color: "#17479e" }}
        >
          Book Reprocess Status
        </Typography>
      </Box> */}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Fade in={!isLoading}>
          <Paper
            sx={{
              width: "100%",
              overflow: "hidden",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "none",
            }}
          >
            <TableContainer sx={{ maxHeight: "70vh" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      "Book Title",
                      "Parse",
                      "EPUB",
                      "Index",
                      "Created",
                      "Duration",
                      "Actions",
                    ].map((label, idx, arr) => (
                      <TableCell
                        key={label}
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          backgroundColor: "#17479e",
                          whiteSpace: "nowrap",
                          borderColor: "#17479e",
                          position: "sticky",
                          top: 0,
                          ...(idx === arr.length - 1
                            ? { right: 0, zIndex: 4 }
                            : { zIndex: 3 }),
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                        No reprocess records found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, index) => {
                      const created = formatDateParts(row.createdAt);
                      const accent =
                        ROW_ICON_COLORS[index % ROW_ICON_COLORS.length];
                      return (
                        <TableRow
                          key={row.id}
                          sx={{
                            "&:hover": { backgroundColor: "action.hover" },
                            "&:last-child td": { borderBottom: 0 },
                          }}
                        >
                          <TableCell sx={{ borderColor: "divider" }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <Avatar
                                variant="rounded"
                                sx={{
                                  bgcolor: `${accent}1f`,
                                  color: accent,
                                  width: 34,
                                  height: 34,
                                  borderRadius: 2,
                                }}
                              >
                                <DescriptionIcon fontSize="small" />
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700 }}
                                  noWrap
                                >
                                  {row.bookName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ display: "block" }}
                                >
                                  Book ID: {row.bookId} ·{" "}
                                  {row.bookPartTypeShortName ||
                                    row.bookPartType}{" "}
                                  · Attempt {row.attempts}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderColor: "divider" }}>
                            <Tooltip title={row.statusSummary || ""} arrow>
                              <span>
                                <StatusChip status={row.status} />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ borderColor: "divider" }}>
                            <Tooltip title={row.epubSummary || ""} arrow>
                              <span>
                                <StatusChip status={row.epubStatus} />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ borderColor: "divider" }}>
                            <Tooltip title={row.indexSummary || ""} arrow>
                              <span>
                                <StatusChip status={row.indexStatus} />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ borderColor: "divider" }}>
                            <Typography variant="body2">
                              {created.date}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {created.time}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderColor: "divider" }}>
                            {formatDuration(row.startedAt, row.finishedAt)}
                          </TableCell>
                          <TableCell
                            sx={{
                              borderColor: "divider",
                              position: "sticky",
                              right: 0,
                              backgroundColor: "#fff",
                              zIndex: 1,
                            }}
                          >
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Tooltip
                                title={
                                  hasReportDetails(row)
                                    ? ""
                                    : "No details available"
                                }
                                arrow
                              >
                                <Typography
                                  component={
                                    hasReportDetails(row) ? "a" : "span"
                                  }
                                  onClick={() =>
                                    hasReportDetails(row) && setDetailsRow(row)
                                  }
                                  variant="body2"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    fontWeight: 700,
                                    color: hasReportDetails(row)
                                      ? "#17479e"
                                      : "text.disabled",
                                    cursor: hasReportDetails(row)
                                      ? "pointer"
                                      : "default",
                                    "&:hover": hasReportDetails(row)
                                      ? { textDecoration: "underline" }
                                      : {},
                                  }}
                                >
                                  <InfoOutlinedIcon fontSize="medium" />
                                </Typography>
                              </Tooltip>
                              <Tooltip title="Retry" arrow>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={retryingId === row.id}
                                    onClick={() => handleRetry(row)}
                                    sx={{
                                      border: "1px solid",
                                      borderColor: "#17479e",
                                      borderRadius: 2,
                                      color: "#17479e",
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(23, 71, 158, 0.08)",
                                      },
                                    }}
                                  >
                                    {retryingId === row.id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <ReplayIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ borderTop: "1px solid", borderColor: "divider" }}
            />
          </Paper>
        </Fade>
      )}

      {/* Details dialog */}
      <Dialog
        open={Boolean(detailsRow)}
        onClose={() => setDetailsRow(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 3,
            py: 2,
            background: "linear-gradient(135deg, #17479e 0%, #2f6fdb 100%)",
            color: "#fff",
          }}
        >
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff" }}>
            <InfoOutlinedIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
              {detailsRow?.bookName}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Reprocess run details
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDetailsRow(null)}
            size="small"
            sx={{ color: "#fff" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent
          sx={{ px: 3, py: 1, maxHeight: "60vh", overflow: "auto" }}
        >
          <Stack divider={<Divider />}>
            <ReportSection label="Parse" data={detailsRow?.report} />
            <ReportSection label="EPUB" data={detailsRow?.epubReport} />
            <ReportSection label="Index" data={detailsRow?.indexReport} />
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BookReprocessStatus;
